<template>
	<Background
		v-if="formattedData"
		color="rgba(255,255,255,0.3)"
		:target="formattedData.target ? formattedData.target.slug : 'all'"
		:spacingTop="true"
	>
		<div class="page page-event">
			<MainColumn>
				
				<Breadcrumbs
					:target="formattedData.target ? formattedData.target.slug : 'all'"
					:items="breadcrumbItems"
				/>
				<v-card class="card" tile v-if="formattedData">
					<!-- Header dell'evento (immagine, titolo, etc) -->
					<EventHeader :eventSlug="eventSlug" />
					
					<!-- Contenuto blocchi -->
					<v-row class="content">
						<v-col offset="2" cols="8">
							<div class="event-info">
								
								<!-- Data evento -->
								<div class="date font-size-25" v-html="formattedEventDate()"></div>

								<!-- Orari e Location -->
								<div class="metadata">
									<div class="time" v-if="eventTime()">
										<b>{{ eventTime() }}</b>
									</div>
									
									<div class="location-category">
										<span v-if="formattedData.event.location">
											{{ formattedData.event.location }}
										</span>
										<span v-if="formattedData.event.location && formattedData.event.target"> - </span>
										<span 
											v-if="formattedData.event.target" 
											:class="'color-' + formattedData.target.slug"
											class="category-badge"
										>
											{{ formattedData.target.name }}
										</span>
									</div>
								</div>

								<!-- Descrizione -->
								<div class="description" v-html="formattedData.event.description"></div>
								<h2 class="booking-title">Prenota</h2>
							</div>
							<RenderBlocks
								class="remove-offset-from-parent"
								:blocks="formattedData.blocks"
								:padding="true"
							/>
						</v-col>
					</v-row>
				</v-card>
			</MainColumn>
		</div>
	</Background>
</template>

<script>
import Background from "@/components/ui/Background";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import RenderBlocks from "@/components/blocks/RenderBlocks";
import EventHeader from "@/views/Event/EventHeader";
import { formatCategoriesAndTargets } from "@/mixins/formatCategoriesAndTargets";
import { GET_EVENT, GET_EVENT_PREVIEW } from "@/queries/Events.js";

export default {
	metaInfo() {
		if (this.seo) {
			return {
				title: this.seo.title,
				meta: [
					{
						name: "description",
						content: this.seo.metaDesc
					},
					{
						name: "keywords",
						content: this.seo.metaKeywords
							? this.seo.metaKeywords
							: this.defaultMetaKeywords
					},
					{
						name: "author",
						content: this.seo.opengraphSiteName
					},
					{
						property: "og:title",
						content: this.seo.title
					},
					{
						property: "og:type",
						content: this.seo.opengraphType
					},
					{
						property: "og:url",
						content: this.seo.opengraphUrl
					},
					{
						property: "og:description",
						content: this.seo.metaDesc
					},
					{
						property: "og:image",
						content: this.seo.opengraphImage
							? this.seo.opengraphImage.sourceUrl
							: this.defaultUrlImage
					},
					{ name: "robots", content: "index,follow" }
				]
			};
		}
	},
	components: {
		EventHeader,
		Background,
		Breadcrumbs,
		RenderBlocks,
	},
	mixins: [formatCategoriesAndTargets],
	computed: {
			pageColor() {
				return this.formattedData && this.formattedData.target 
					? this.colorsObject[this.formattedData.target.slug]
					: this.colorsObject['all'];
			},
	},
	methods: {
		addZero(i) {
			if (i < 10) {
				i = "0" + i;
			}
			return i;
		},
		formattedEventDate() {
			if (!this.formattedData || !this.formattedData.event) return '';
			
			const months = {
				0: "Gennaio", 1: "Febbraio", 2: "Marzo", 3: "Aprile",
				4: "Maggio", 5: "Giugno", 6: "Luglio", 7: "Agosto",
				8: "Settembre", 9: "Ottobre", 10: "Novembre", 11: "Dicembre"
			};
			
			const event = this.formattedData.event;
			let startDateObj, endDateObj;
			
			// Gestisce slot tickets se presenti
			if (event.actSlotTickets && event.actSlotTickets.length > 0) {
				startDateObj = new Date(event.actSlotTickets[0].startSlot);
				endDateObj = new Date(event.actSlotTickets[event.actSlotTickets.length - 1].endSlot);
			} else {
				startDateObj = new Date(event.startDate);
				endDateObj = event.endDate ? new Date(event.endDate) : null;
			}
			
			const startDate = {
				year: startDateObj.getUTCFullYear(),
				month: startDateObj.getUTCMonth(),
				day: startDateObj.getUTCDate()
			};
			
			// Se non c'è data fine
			if (!endDateObj) {
				return `<b>${startDate.day} ${months[startDate.month]}</b>`;
			}
			
			const endDate = {
				year: endDateObj.getUTCFullYear(),
				month: endDateObj.getUTCMonth(),
				day: endDateObj.getUTCDate()
			};
			
			// Stesso anno
			if (startDate.year === endDate.year) {
				// Stesso mese
				if (startDate.month === endDate.month) {
					// Stesso giorno
					if (startDate.day === endDate.day) {
						return `<b>${startDate.day} ${months[endDate.month]}</b>`;
					}
					// Giorni diversi, stesso mese
					return `Dal <b>${startDate.day}</b> al <b>${endDate.day} ${months[endDate.month]}</b>`;
				}
				// Mesi diversi
				return `Dal <b>${startDate.day} ${months[startDate.month]}</b> al <b>${endDate.day} ${months[endDate.month]}</b>`;
			}
			
			// Anni diversi
			return `Dal <b>${startDate.day} ${months[startDate.month]} ${startDate.year}</b> al <b>${endDate.day} ${months[endDate.month]} ${endDate.year}</b>`;
		},
		eventTime() {
			if (!this.formattedData || !this.formattedData.event) return null;
			
			const event = this.formattedData.event;
			let startDateObj, endDateObj;
			
			// Gestisce slot tickets se presenti
			if (event.actSlotTickets && event.actSlotTickets.length > 0) {
				startDateObj = new Date(event.actSlotTickets[0].startSlot);
				endDateObj = new Date(event.actSlotTickets[event.actSlotTickets.length - 1].endSlot);
			} else {
				startDateObj = new Date(event.startDate);
				endDateObj = event.endDate ? new Date(event.endDate) : null;
			}
			
			const startHours = this.addZero(startDateObj.getHours());
			const startMinutes = this.addZero(startDateObj.getMinutes());
			
			// Se non c'è orario di fine o è lo stesso giorno senza orario specifico
			if (!endDateObj) {
				return `${startHours}:${startMinutes}`;
			}
			
			const endHours = this.addZero(endDateObj.getHours());
			const endMinutes = this.addZero(endDateObj.getMinutes());
			
			// Controlla se è lo stesso giorno
			const sameDay = startDateObj.getDate() === endDateObj.getDate() &&
							startDateObj.getMonth() === endDateObj.getMonth() &&
							startDateObj.getFullYear() === endDateObj.getFullYear();
			
			if (sameDay) {
				return `${startHours}:${startMinutes} / ${endHours}:${endMinutes}`;
			}
			
			// Se giorni diversi, mostra solo orario inizio
			return `${startHours}:${startMinutes}`;
		}
	},
	data() {
		return {
			seo: false,
			breadcrumbItems: [],
			formattedData: null,
			eventSlug: null
		};
	},
	async mounted() {
		this.loadStart();
		this.eventSlug = this.$route.params.eventSlug;
		
		let query;
		let isPreview = this.$route.query && this.$route.query.preview;
		
		if (isPreview) {
			query = {
				query: GET_EVENT_PREVIEW(this.eventSlug)
			};
		} else {
			query = {
				query: GET_EVENT(this.eventSlug)
			};
		}
		
		let data = await this.$apollo.query(query);

		console.log(data);
		
		if (isPreview) {
			data = data && data.data && data.data.event || false;
		} else {
			data = data && data.data && data.data.events && data.data.events.nodes && data.data.events.nodes[0] 
				? data.data.events.nodes[0] 
				: false;
		}
		
		if (data) {
			this.seo = data.seo || null;
			data.blocks = JSON.parse("[" + data.blocksJSON + "]")[0];
			let newData = this.formatCategoriesAndTargets(data)[0];
			
			this.formattedData = {
				title: newData.title,
				event: data.blocks[0].attributes,
				blocks: newData.blocks.slice(1, newData.length), // Salta il primo blocco (usato da EventHeader)
				srcImage: newData.featuredImage
					? newData.featuredImage.node.sourceUrl
					: "",
				target: newData.target
			};
			
			this.breadcrumbItems = [
				{
					text: "Eventi",
					to: null
				}
			];
			
			this.loadDone();
		} else {
			this.get404();
		}
	}
};
</script>

<style lang="scss" scoped>
.booking-title {
	text-transform: uppercase;
	text-align: center;
	margin-top: 2rem;
}
.metadata {
	display: flex;
	gap: 0.5rem;
	text-transform: uppercase;
	font-size: 0.8rem;
	margin: 10px 0;
}

.page {
	margin: auto;
	margin-bottom: $spacing-1;
}
.card {
	min-height: 1000px;
}
@media only screen and (max-width: 550px) {
	.card {
		padding: 0 $spacing-0;
		max-width: 100vw;
		left: 50%;
		position: relative;
		transform: translateX(-50%);
	}
}
</style>

<style lang="scss">
	.remove-offset-from-parent {
		.offset-1 {
			margin-left: 0 !important;
		}

		.col-10 {
			max-width: 100% !important;
			flex: 0 0 100% !important;
		}
	}

	.page-event {
		.form {
			margin-bottom: 5rem;
		}
	}

</style>